import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams, useLocation, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogos";
import PhoneInput from "@/components/PhoneInput";
import ProofUpload from "@/components/ProofUpload";
import KycUpload from "@/components/KycUpload";
import api, { formatApiError } from "@/lib/api";
import { pushLocalTx } from "@/lib/localHistory";
import { trackMetaEvent } from "@/lib/meta";
import { useAuth } from "@/context/AuthContext";
import {
  CORRIDORS, DEPOSIT_METHODS, RECEPTION_METHODS,
  depositMethodsFor, receptionMethodsFor, formatAmount,
  validatePhone, PHONE_VALIDATION, detectOperatorFromPhone,
} from "@/lib/constants";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Copy, CheckCircle2, ShieldCheck, FileText, IdCard } from "lucide-react";

const KYC_THRESHOLD = 300000;
const MIN_DEPOSIT = 2000;
const MIN_DEPOSIT_BY_CORRIDOR = {
  tg_ga: 2000,    // XOF
  ga_fr: 2000,    // XAF
  ga_rw: 2000,    // XAF
  fr_ga: 20,      // EUR — PayPal F&F minimum
};
const minDepositFor = (corridor) => MIN_DEPOSIT_BY_CORRIDOR[corridor] ?? MIN_DEPOSIT;
// Per-corridor minimum amount (XAF) that makes the referral code meaningful (5€ commission earned)
const REFERRAL_MIN_BY_CORRIDOR = {
  ga_fr: 200000,
  ga_rw: 100000,
};
const referralMinFor = (corridor) => REFERRAL_MIN_BY_CORRIDOR[corridor] ?? 100000;

const SUPPORT_AGENT_WA = "https://wa.me/33753728195?text=" + encodeURIComponent(
  "Bonjour, je n'ai pas de compte PayPal. Pouvez-vous m'accompagner pour mon échange FR → Gabon ?"
);
// Sanitize person-name inputs in real-time: keep only letters (incl. accents), spaces, hyphens, apostrophes, dots
const sanitizeName = (raw) => (raw || "").replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'\-.]/g, "");
const KYC_DOC_LABELS = {
  cni: "Carte d'identité (CNI)",
  passeport: "Passeport",
  permis: "Permis de conduire",
};
const STEP_LABELS_BASE = ["Corridor", "Bénéficiaire", "Montant", "Dépôt", "Confirmation"];
const STEP_LABELS_KYC = ["Corridor", "Bénéficiaire", "Montant", "KYC", "Dépôt", "Confirmation"];

// URL segment ↔ semantic step label. Same slugs for both BASE and KYC flows since
// each semantic exists at most once per flow.
const STEP_SLUG_TO_SEMANTIC = {
  "": "Corridor",
  "corridor": "Corridor",
  "beneficiaire": "Bénéficiaire",
  "montant": "Montant",
  "kyc": "KYC",
  "depot": "Dépôt",
  "confirmation": "Confirmation",
};
const SEMANTIC_TO_STEP_SLUG = {
  "Corridor": "",
  "Bénéficiaire": "beneficiaire",
  "Montant": "montant",
  "KYC": "kyc",
  "Dépôt": "depot",
  "Confirmation": "confirmation",
};

export default function SendMoney() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const routeParams = useParams();
  const location = useLocation();
  const initCorridor = CORRIDORS[params.get("corridor")]
    ? params.get("corridor")
    : (CORRIDORS[user?.preferred_corridor] ? user.preferred_corridor : "tg_ga");
  const presetAmount = params.get("amount") || "";
  const reuseCode = params.get("reuse") || "";
  const templateId = params.get("template") || "";
  // Landing → /envoyer?corridor=xxx : if a valid corridor is prefilled in the URL, jump directly to step 1 (Bénéficiaire).
  // Logged-in user with a preferred corridor also skips the corridor step (their pref is used as initCorridor).
  const skipCorridorStep = !!CORRIDORS[params.get("corridor")] || !!CORRIDORS[user?.preferred_corridor];
  const urlEtape = routeParams?.etape || "";
  // Compute the initial semantic step from URL. Priority :
  //  1) explicit slug in the path (/envoyer/beneficiaire, /envoyer/montant, ...)
  //  2) corridor query param on /envoyer (from Landing) → jump to Bénéficiaire
  //  3) default to Corridor
  let initialSemantic;
  if (urlEtape) {
    initialSemantic = STEP_SLUG_TO_SEMANTIC[urlEtape] || "Corridor";
  } else if (skipCorridorStep) {
    initialSemantic = "Bénéficiaire";
  } else {
    initialSemantic = "Corridor";
  }
  const initialStepBase = STEP_LABELS_BASE.indexOf(initialSemantic);
  const [step, setStep] = useState(initialStepBase >= 0 ? initialStepBase : 0);

  // Meta Pixel: a client starts a transfer (once per visit to the send flow)
  useEffect(() => {
    trackMetaEvent("InitiateCheckout", { content_category: "transfert" });
  }, []);

  // Warn user before leaving the page mid-process (tab close / refresh / navigate away)
  useEffect(() => {
    const inProgress = step > 0 && step < steps.length - 1;
    if (!inProgress) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Êtes-vous sûr de vouloir annuler cette transaction ? Si vous quittez cette page, vous devrez recommencer depuis le début.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Sync URL when step changes internally (so back/forward + shareable URLs work + ads landing pages work)
  useEffect(() => {
    const stepsArr = steps;
    const semantic = stepsArr[step] || "Corridor";
    const slug = SEMANTIC_TO_STEP_SLUG[semantic] || "";
    const desired = slug ? `/envoyer/${slug}` : "/envoyer";
    // Keep the existing query string (corridor, reuse, template, amount)
    const currentPath = location.pathname;
    if (currentPath !== desired) {
      navigate(`${desired}${location.search || ""}`, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Sync step when URL changes externally (back/forward button, ad landing, deep link)
  useEffect(() => {
    const slug = (routeParams?.etape || "").toLowerCase();
    const semantic = STEP_SLUG_TO_SEMANTIC[slug];
    if (!semantic) return;
    const stepsArr = steps;
    const idx = stepsArr.indexOf(semantic);
    if (idx >= 0 && idx !== step) {
      setStep(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams?.etape]);

  // Race-condition fix: on cold navigation to /envoyer, the user is null on first render
  // (AuthProvider fetches /auth/me asynchronously). When user resolves and has a
  // preferred_corridor, jump to Bénéficiaire step AND update the form corridor — but only if
  // we haven't already moved past Corridor and no explicit slug/query overrides it.
  useEffect(() => {
    if (!user?.preferred_corridor) return;
    const pref = user.preferred_corridor;
    if (!CORRIDORS[pref]) return;
    if (urlEtape) return;                              // URL explicitly requests a step → respect it
    if (params.get("corridor")) return;                 // URL query already sets corridor → already handled
    if (step !== 0) return;                             // user already advanced past Corridor
    // Update form.corridor + dial codes + methods if it isn't already the preferred one.
    // Compare against current form state (not initCorridor, which recalculates on re-render).
    setForm((p) => {
      if (p.corridor === pref) return p;
      const cdef = CORRIDORS[pref];
      return {
        ...p,
        corridor: pref,
        sender_phone: cdef.senderDialCode + " ",
        beneficiary_phone: cdef.beneficiaryDialCode + " ",
        reception_method: receptionMethodsFor(pref)[0][0],
        deposit_method: depositMethodsFor(pref)[0][0],
      };
    });
    const beneficiaireIdx = STEP_LABELS_BASE.indexOf("Bénéficiaire");
    if (beneficiaireIdx > 0) setStep(beneficiaireIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.preferred_corridor]);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [amountMode, setAmountMode] = useState("send"); // "send" (source) | "receive" (target)
  const [receiveInput, setReceiveInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showBenefList, setShowBenefList] = useState(false);
  const [trustedBeneficiary, setTrustedBeneficiary] = useState(false);
  const [savedContacts, setSavedContacts] = useState([]);
  const [frgaAccounts, setFrgaAccounts] = useState(null);
  const [referralDiscount, setReferralDiscount] = useState(5);

  useEffect(() => {
    api.get("/referral-config-public").then(({ data }) => {
      if (data?.discount_percent != null) setReferralDiscount(data.discount_percent);
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    corridor: initCorridor,
    sender_name: "",
    sender_phone: CORRIDORS[initCorridor].senderDialCode + " ",
    sender_email: "",
    beneficiary_name: "",
    beneficiary_phone: CORRIDORS[initCorridor].beneficiaryDialCode + " ",
    beneficiary_email: "",
    beneficiary_account: "",
    reception_method: receptionMethodsFor(initCorridor)[0][0],
    amount_xof: presetAmount,
    deposit_method: depositMethodsFor(initCorridor)[0][0],
    payment_reference: "",
    proof_path: "",
    note: "",
    // KYC (only required when amount_xof >= KYC_THRESHOLD)
    kyc_doc_type: "cni",
    kyc_doc_path: "",
    kyc_full_name: "",
    kyc_doc_number: "",
    // Referral / promo code — applies 15% fee discount on ga_fr corridor
    referral_code: (params.get("ref") || "").toUpperCase(),
  });

  const requiresKyc = parseFloat(form.amount_xof || 0) >= KYC_THRESHOLD;
  const steps = requiresKyc ? STEP_LABELS_KYC : STEP_LABELS_BASE;
  // Clamp the active step index whenever the steps list shrinks (e.g. user lowered amount below 300k)
  useEffect(() => {
    if (step > steps.length - 1) {
      setStep(steps.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);
  const currentSemantic = steps[step] || "Corridor";

  // Prefill sender fields from logged-in user + their latest transaction
  useEffect(() => {
    if (!user || prefilled) return;
    (async () => {
      let lastTx = null;
      try {
        const { data } = await api.get("/me/transactions");
        lastTx = data?.[0] || null;
      } catch (_e) { /* ignore */ }
      setForm((p) => ({
        ...p,
        sender_name: p.sender_name || lastTx?.sender_name || user.name || "",
        sender_email: p.sender_email || lastTx?.sender_email || user.email || "",
        // Registered client: always use their account phone — never re-ask.
        sender_phone: user.phone || p.sender_phone,
      }));
      setPrefilled(true);

      // Also fetch the beneficiary address book
      try {
        const { data } = await api.get("/me/beneficiaries");
        setBeneficiaries(data || []);
      } catch (_e) { /* ignore */ }
    })();
  }, [user, prefilled]);

  const applyBeneficiary = (b) => {
    setForm((p) => ({
      ...p,
      beneficiary_name: b.beneficiary_name || b.name || "",
      beneficiary_phone: b.beneficiary_phone || b.phone || (CORRIDORS[p.corridor].beneficiaryDialCode + " "),
      beneficiary_email: b.beneficiary_email || b.email || "",
      beneficiary_account: b.beneficiary_account || b.account || "",
      reception_method: b.reception_method && receptionMethodsFor(p.corridor).some(([k]) => k === b.reception_method)
        ? b.reception_method
        : p.reception_method,
    }));
    // Mark beneficiary data as user-trusted — skip strict phone regex (the saved value was previously validated when stored,
    // and we don't want UI mismatches to block proceeding when picking from the contact book).
    setTrustedBeneficiary(true);
    setShowBenefList(false);
    toast.success(`Bénéficiaire « ${b.beneficiary_name || b.name} » sélectionné`);
    // Auto-advance to Montant for logged-in users → 2-step shortcut flow
    if (user) {
      setTimeout(() => {
        setStep((s) => {
          const stepsArr = requiresKyc ? STEP_LABELS_KYC : STEP_LABELS_BASE;
          const benefIdx = stepsArr.indexOf("Bénéficiaire");
          return benefIdx >= 0 && s <= benefIdx ? benefIdx + 1 : s;
        });
      }, 350);
    }
  };

  // Load user's saved contacts (book) for the picker — once user is known
  useEffect(() => {
    if (!user) { setSavedContacts([]); return; }
    let cancelled = false;
    api.get("/me/contacts").then(({ data }) => {
      if (!cancelled) setSavedContacts(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Pré-remplir depuis une transaction passée (?reuse=SL-XXXXXXXX)
  useEffect(() => {
    if (!reuseCode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/transactions/track/${reuseCode}`);
        if (cancelled) return;
        const cKey = CORRIDORS[data.corridor] ? data.corridor : "tg_ga";
        const cdef = CORRIDORS[cKey];
        const depMatches = depositMethodsFor(cKey).some(([k]) => k === data.deposit_method);
        const recMatches = receptionMethodsFor(cKey).some(([k]) => k === data.reception_method);
        // Skip the next corridor-reset effect only when corridor actually changes
        if (cKey !== initCorridor) {
          skipCorridorResetRef.current = true;
        }
        setForm((p) => ({
          ...p,
          corridor: cKey,
          beneficiary_name: data.beneficiary_name || "",
          beneficiary_phone: data.beneficiary_phone || (cdef.beneficiaryDialCode + " "),
          beneficiary_email: data.beneficiary_email || "",
          beneficiary_account: data.beneficiary_account || "",
          reception_method: recMatches ? data.reception_method : receptionMethodsFor(cKey)[0][0],
          deposit_method: depMatches ? data.deposit_method : depositMethodsFor(cKey)[0][0],
          amount_xof: presetAmount || (data.amount_xof != null ? String(data.amount_xof) : ""),
          sender_name: p.sender_name || data.sender_name || "",
          sender_phone: p.sender_phone && p.sender_phone.replace(/\D/g, "").length > cdef.senderDialCode.replace(/\D/g, "").length
            ? p.sender_phone
            : (data.sender_phone || (cdef.senderDialCode + " ")),
          sender_email: p.sender_email || data.sender_email || "",
          proof_path: "", // never reuse the proof
          payment_reference: "",
        }));
        toast.success(`Bénéficiaire « ${data.beneficiary_name} » pré-rempli depuis ${data.code}`);
      } catch (_e) {
        toast.error("Impossible de pré-remplir : transaction introuvable.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reuseCode]);

  // Pré-remplir depuis un modèle utilisateur (?template=<id>)
  useEffect(() => {
    if (!templateId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/me/templates/${templateId}`);
        if (cancelled) return;
        const cKey = CORRIDORS[data.corridor] ? data.corridor : "tg_ga";
        const cdef = CORRIDORS[cKey];
        const recMatches = receptionMethodsFor(cKey).some(([k]) => k === data.reception_method);
        if (cKey !== initCorridor) {
          skipCorridorResetRef.current = true;
        }
        setForm((p) => ({
          ...p,
          corridor: cKey,
          beneficiary_name: data.beneficiary_name || "",
          beneficiary_phone: data.beneficiary_phone || (cdef.beneficiaryDialCode + " "),
          beneficiary_email: data.beneficiary_email || "",
          beneficiary_account: data.beneficiary_account || "",
          reception_method: recMatches ? data.reception_method : receptionMethodsFor(cKey)[0][0],
          amount_xof: presetAmount || (data.suggested_amount != null ? String(data.suggested_amount) : p.amount_xof),
        }));
        toast.success(`Modèle « ${data.name} » chargé.`);
        // Auto-advance to beneficiary step
        setStep((s) => Math.max(s, 1));
      } catch (_e) {
        toast.error("Modèle introuvable ou inaccessible.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, user]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const c = CORRIDORS[form.corridor];

  // FR → GA deposit accounts (admin-configurable PayPal email / Wero number+name).
  useEffect(() => {
    if (form.corridor !== "fr_ga") return;
    let cancelled = false;
    api.get("/settings?corridor=fr_ga")
      .then(({ data }) => { if (!cancelled) setFrgaAccounts(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.corridor]);

  // When corridor changes, reset method defaults AND reset phone prefixes
  // (skip while a "reuse" prefill is in progress, otherwise it would erase reused fields)
  const skipCorridorResetRef = useRef(false);
  useEffect(() => {
    if (skipCorridorResetRef.current) {
      skipCorridorResetRef.current = false;
      return;
    }
    const cdef = CORRIDORS[form.corridor];
    const dial = cdef.senderDialCode;
    // Registered clients keep their account phone across corridors (we never re-ask it);
    // anonymous senders get a dial-code prefix to fill in.
    setForm((p) => ({
      ...p,
      deposit_method: depositMethodsFor(p.corridor)[0][0],
      reception_method: receptionMethodsFor(p.corridor)[0][0],
      sender_phone: user?.phone ? user.phone : (dial + " "),
      beneficiary_phone: cdef.beneficiaryDialCode + " ",
    }));
    setReceiveInput("");
  }, [form.corridor, user]);

  // Live quote — supports both directions ("send" = amount_xof, "receive" = amount_receive).
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ corridor: form.corridor });
        if (form.referral_code && (form.corridor === "ga_fr" || form.corridor === "ga_rw")) {
          params.set("referral_code", form.referral_code);
        }
        if (amountMode === "receive") {
          const recv = parseFloat(receiveInput);
          if (!recv || recv <= 0) { setQuote(null); return; }
          params.set("amount_receive", String(recv));
        } else {
          const amt = parseFloat(form.amount_xof);
          if (!amt || amt <= 0) { setQuote(null); return; }
          params.set("amount_xof", String(amt));
        }
        const { data } = await api.get(`/quote?${params.toString()}`);
        if (cancelled) return;
        setQuote(data);
        // In "receive" mode, the computed send amount becomes the source of truth.
        if (amountMode === "receive" && data?.amount_xof != null && String(data.amount_xof) !== form.amount_xof) {
          update("amount_xof", String(data.amount_xof));
        }
      } catch (_e) { if (!cancelled) setQuote(null); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.amount_xof, receiveInput, amountMode, form.corridor, form.referral_code]);

  // Meta Pixel: ViewContent when a quote is displayed (once per corridor per visit)
  const viewContentSentRef = useRef(new Set());
  useEffect(() => {
    if (!quote || viewContentSentRef.current.has(form.corridor)) return;
    viewContentSentRef.current.add(form.corridor);
    trackMetaEvent("ViewContent", {
      value: parseFloat(form.amount_xof) || 0,
      currency: CORRIDORS[form.corridor]?.source_currency || "XOF",
      content_ids: [form.corridor],
      content_type: "product",
      content_name: `transfert_${form.corridor}`,
      content_category: "transfert",
    });
  }, [quote, form.corridor, form.amount_xof]);

  // ===== Operator auto-detection (auto-switch silently to avoid manual mistakes) =====
  // Watch sender_phone vs deposit_method (only meaningful for tg_ga where we have 2 deposit methods)
  const lastSenderSuggestRef = useRef("");
  useEffect(() => {
    if (form.corridor !== "tg_ga") return;
    const detected = detectOperatorFromPhone(form.sender_phone, c.senderDialCode, "TG");
    if (!detected || detected === form.deposit_method) return;
    const key = `${detected}:${form.sender_phone}`;
    if (lastSenderSuggestRef.current === key) return;
    lastSenderSuggestRef.current = key;
    const suggested = DEPOSIT_METHODS[detected];
    update("deposit_method", detected);
    toast.success(`Portefeuille adapté : ${suggested.label}`, {
      description: `Détection automatique d'après votre numéro.`,
      duration: 3000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sender_phone, form.deposit_method, form.corridor]);

  // Watch beneficiary_phone vs reception_method (only for tg_ga; ga_fr has no operator concept)
  const lastBeneficiarySuggestRef = useRef("");
  useEffect(() => {
    if (form.corridor !== "tg_ga") return;
    const detected = detectOperatorFromPhone(form.beneficiary_phone, c.beneficiaryDialCode, "GA");
    if (!detected || detected === form.reception_method) return;
    const key = `${detected}:${form.beneficiary_phone}`;
    if (lastBeneficiarySuggestRef.current === key) return;
    lastBeneficiarySuggestRef.current = key;
    const suggested = RECEPTION_METHODS[detected];
    update("reception_method", detected);
    toast.success(`Réception adaptée : ${suggested.label}`, {
      description: `Détection automatique d'après le numéro du bénéficiaire.`,
      duration: 3000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.beneficiary_phone, form.reception_method, form.corridor]);

  const isFR = form.corridor === "ga_fr";
  const isRW = form.corridor === "ga_rw";
  const isFRGA = form.corridor === "fr_ga";  // France → Gabon (PayPal F&F)
  // Reception uses an account (email / IBAN / Wero) instead of a phone? Only for ga_fr.
  const usesAccountReception = isFR;
  // Receiving country phrasing
  const receiverLocLabel = isFR ? "en France" : isRW ? "au Rwanda" : "au Gabon";
  // Sender country phrasing
  const senderLocLabel = isFRGA ? "en France" : (isFR || isRW) ? "au Gabon" : "au Togo";
  const recMeta = RECEPTION_METHODS[form.reception_method];

  // Sender phone validation skipped for FR→GA (sender uses PayPal email instead)
  const senderValidation = isFRGA
    ? { valid: !!form.sender_email, message: form.sender_email ? "" : "Email PayPal requis" }
    : validatePhone(form.deposit_method, form.sender_phone, c.senderDialCode);

  // Beneficiary phone validation:
  // - tg_ga: against reception_method (airtel_gabon / moov_gabon)
  // - ga_fr: optional but if filled, validate against france_any
  const beneficiaryValidation = (() => {
    if (isFR) {
      const digits = (form.beneficiary_phone || "").replace(/\D/g, "").replace(/^33/, "");
      if (!digits) return { valid: true, message: "" }; // optional
      return validatePhone("france_any", form.beneficiary_phone, c.beneficiaryDialCode);
    }
    return validatePhone(form.reception_method, form.beneficiary_phone, c.beneficiaryDialCode);
  })();

  const canNext = () => {
    if (currentSemantic === "Corridor") return !!form.corridor;
    if (currentSemantic === "Bénéficiaire") {
      // Logged-in users: sender info mainly comes from profile, but we still require a usable phone/email
      // (the inline completion input is shown when the profile value doesn't match the current corridor).
      if (!form.sender_name) return false;
      if (user) {
        // Registered clients: the sender phone comes from their profile and is never
        // re-asked here, so we don't gate on it (fr_ga still needs the PayPal email).
        if (isFRGA && !form.sender_email) return false;
      } else {
        if (!senderValidation.valid) return false;
      }
      if (!form.beneficiary_name || !form.reception_method) return false;
      if (isFR) {
        if (recMeta?.fields?.includes("email") && !form.beneficiary_email) return false;
        if (recMeta?.fields?.includes("account") && !form.beneficiary_account) return false;
      } else {
        // Trusted beneficiary (picked from contact book or derived list) bypasses strict phone regex —
        // we already trust the saved value. Only require that the phone is non-empty.
        if (trustedBeneficiary) {
          if (!form.beneficiary_phone || form.beneficiary_phone.trim().length < 3) return false;
        } else if (!beneficiaryValidation.valid) {
          return false;
        }
      }
      return true;
    }
    if (currentSemantic === "Montant") return parseFloat(form.amount_xof) >= minDepositFor(form.corridor) && quote;
    if (currentSemantic === "KYC") {
      return !!form.kyc_doc_path && !!form.kyc_doc_type && !!(form.kyc_full_name || "").trim() && !!(form.kyc_doc_number || "").trim();
    }
    if (currentSemantic === "Dépôt") return form.deposit_method && form.proof_path;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount_xof: parseFloat(form.amount_xof),
        // For FR→GA the sender does not have a phone — store the PayPal email in sender_phone so backend validation accepts it.
        sender_phone: isFRGA ? (form.sender_email || "PayPal") : form.sender_phone,
        sender_email: form.sender_email || undefined,
        beneficiary_email: form.beneficiary_email || undefined,
        beneficiary_account: form.beneficiary_account || undefined,
        beneficiary_phone: form.beneficiary_phone || undefined,
        payment_reference: form.payment_reference || undefined,
        proof_path: form.proof_path || undefined,
        // KYC: only include if required (otherwise let backend ignore)
        kyc_doc_path: requiresKyc ? form.kyc_doc_path : undefined,
        kyc_doc_type: requiresKyc ? form.kyc_doc_type : undefined,
        kyc_full_name: requiresKyc ? (form.kyc_full_name || "").trim() : undefined,
        kyc_doc_number: requiresKyc ? (form.kyc_doc_number || "").trim() : undefined,
        // Promo code: applicable to any amount for ga_fr / ga_rw (5 % discount under cumul system)
        referral_code: form.referral_code && (form.corridor === "ga_fr" || form.corridor === "ga_rw") && parseFloat(form.amount_xof) > 0
          ? form.referral_code.toUpperCase()
          : undefined,
        // « Partenaire engagé » attribution (France → Gabon). Captured from ?pe= link.
        partner_code: (localStorage.getItem("sl_pe") || "").toUpperCase() || undefined,
      };
      const { data } = await api.post("/transactions", payload);
      pushLocalTx(data.code);
      // Meta Pixel: transfer completed (conversion)
      trackMetaEvent("Purchase", {
        value: parseFloat(form.amount_xof) || 0,
        currency: CORRIDORS[form.corridor]?.source_currency || "XOF",
        content_ids: [form.corridor],
        content_type: "product",
        content_name: `transfert_${form.corridor}`,
        content_category: "transfert",
      });
      toast.success("Transaction créée !");
      navigate(`/suivi?code=${data.code}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const dep = DEPOSIT_METHODS[form.deposit_method];

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Nouvel envoi</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Envoyez de l'argent</h1>

          {/* Stepper */}
          <div className="mt-8 flex items-center gap-2" data-testid="stepper">
            {steps.map((label, i) => (
              <div key={i} className="flex-1 flex items-center">
                <div className={`flex-1 h-1 rounded-full ${i <= step ? "bg-[var(--sl-primary)]" : "bg-gray-200"}`} />
                {i < steps.length - 1 && <div className="w-2" />}
              </div>
            ))}
          </div>
          <div className={`mt-2 grid text-xs text-gray-500 ${steps.length === 6 ? "grid-cols-6" : "grid-cols-5"}`}>
            {steps.map((s, i) => (
              <div key={s} className={i === step ? "text-[var(--sl-primary)] font-semibold" : ""}>
                {s}
              </div>
            ))}
          </div>

          <div className="sl-card mt-8" data-testid="send-form-card">
            {/* Welcome banner pour utilisateur connecté */}
            {user && prefilled && currentSemantic === "Bénéficiaire" && (
              <div className="mb-6 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-start gap-2" data-testid="prefill-banner">
                <span className="text-base">✓</span>
                <span>Bonjour <strong>{user.name || user.email}</strong>, vos informations sont déjà connues. Indiquez juste qui doit recevoir l&apos;argent ci-dessous.</span>
              </div>
            )}

            {currentSemantic === "Corridor" && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl font-semibold">Choisissez votre corridor</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.values(CORRIDORS).map((co) => (
                    <button
                      key={co.id}
                      type="button"
                      onClick={() => update("corridor", co.id)}
                      className={`p-5 rounded-2xl border-2 text-left transition ${
                        form.corridor === co.id
                          ? "border-[var(--sl-primary)] bg-[var(--sl-primary)]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`corridor-option-${co.id}`}
                    >
                      <div className="text-2xl">{co.flagFrom} → {co.flagTo}</div>
                      <div className="font-heading text-lg font-bold mt-2">{co.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{co.description}</div>
                      <div className="text-xs font-mono text-gray-400 mt-2">{co.source_currency} → {co.target_currency}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentSemantic === "Bénéficiaire" && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl font-semibold">
                  {user ? "Le bénéficiaire" : "Vos informations & celles du bénéficiaire"}
                </h3>
                {!user && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="sl-label">Votre nom complet</label>
                    <input className="sl-input" value={form.sender_name} onChange={(e) => update("sender_name", sanitizeName(e.target.value))} maxLength={80} data-testid="input-sender-name" />
                  </div>
                  {isFRGA ? (
                    <div>
                      <label className="sl-label">Votre email PayPal</label>
                      <input
                        className="sl-input"
                        type="email"
                        value={form.sender_email}
                        onChange={(e) => update("sender_email", e.target.value)}
                        placeholder="vous@example.com"
                        data-testid="input-sender-paypal-email"
                      />
                      <p className="mt-1 text-xs text-gray-500">L&apos;email du compte PayPal depuis lequel vous enverrez les fonds.</p>
                    </div>
                  ) : (
                    <div>
                      <label className="sl-label">Votre téléphone ({c.flagFrom} {c.senderDialCode})</label>
                      <PhoneInput
                        value={form.sender_phone}
                        onChange={(v) => update("sender_phone", v)}
                        dialCode={c.senderDialCode}
                        placeholder="90 00 00 00"
                        data-testid="input-sender-phone"
                        error={form.sender_phone && form.sender_phone.length > c.senderDialCode.length + 2 && !senderValidation.valid ? senderValidation.message : ""}
                        hint={PHONE_VALIDATION[form.deposit_method]?.hint || ""}
                      />
                    </div>
                  )}
                  {!isFRGA && (
                    <div className="md:col-span-2">
                      <label className="sl-label">Votre email (optionnel)</label>
                      <input className="sl-input" type="email" value={form.sender_email} onChange={(e) => update("sender_email", e.target.value)} data-testid="input-sender-email" />
                    </div>
                  )}
                </div>
                )}
                {user && form.sender_name && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="sender-summary-card">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Expéditeur</div>
                        <div className="text-sm font-semibold truncate">{form.sender_name}</div>
                        <div className="text-xs text-gray-600 font-mono truncate">{isFRGA ? (form.sender_email || "— ajouter votre email PayPal") : (form.sender_phone && form.sender_phone.replace(/\D/g,"").length > c.senderDialCode.replace(/\D/g,"").length + 4 ? form.sender_phone : "— numéro manquant (modifier le profil)")}</div>
                      </div>
                      <Link to="/compte" className="text-xs text-[var(--sl-primary)] hover:underline flex-shrink-0" data-testid="edit-sender-profile">Modifier mon profil</Link>
                    </div>
                    {/* Registered clients are never re-asked for their phone — it's taken from their
                        account (fixes the buggy inline phone-completion input). Only FR→GA needs a
                        PayPal email, which we still allow completing inline if missing. */}
                    {isFRGA && !form.sender_email && (
                      <div className="mt-3 pt-3 border-t border-emerald-200/60">
                        <label className="sl-label !text-emerald-800">Votre email PayPal</label>
                        <input
                          className="sl-input"
                          type="email"
                          value={form.sender_email}
                          onChange={(e) => update("sender_email", e.target.value)}
                          placeholder="vous@example.com"
                          data-testid="input-sender-paypal-email-inline"
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className={`${user ? "" : "border-t border-gray-100 pt-5"} grid md:grid-cols-2 gap-4`}>
                  {user && (
                    <div className="md:col-span-2 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid="benef-picker-card">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Bénéficiaire</div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            Choisir un bénéficiaire enregistré ou en ajouter un nouveau ci-dessous.
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(savedContacts.length + beneficiaries.length) > 0 && (
                            <button
                              type="button"
                              onClick={() => setShowBenefList((v) => !v)}
                              className="inline-flex items-center gap-1.5 rounded-full bg-white text-emerald-700 border border-emerald-300 text-xs font-semibold px-3 py-1.5 hover:bg-emerald-50"
                              data-testid="show-benef-list"
                            >
                              📒 Mes bénéficiaires ({savedContacts.length + beneficiaries.length})
                            </button>
                          )}
                          <Link
                            to="/contacts"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white text-gray-700 border border-gray-300 text-xs font-semibold px-3 py-1.5 hover:bg-gray-50"
                            data-testid="link-contacts"
                          >
                            + Ajouter
                          </Link>
                        </div>
                      </div>
                      {showBenefList && (
                        <div className="mt-3 rounded-lg border bg-white p-2 max-h-64 overflow-auto divide-y divide-gray-100" data-testid="benef-list">
                          {[
                            ...savedContacts.filter((c) => !c.corridor || c.corridor === form.corridor).map((c) => ({ kind: "saved", ...c, beneficiary_name: c.name, beneficiary_phone: c.phone, beneficiary_email: c.email, beneficiary_account: c.account })),
                            ...beneficiaries.filter((b) => !b.corridor || b.corridor === form.corridor).filter((b) => !savedContacts.some((c) => c.phone === b.beneficiary_phone || c.email === b.beneficiary_email)),
                          ].slice(0, 30).map((b, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => applyBeneficiary(b)}
                              className="w-full text-left p-2.5 hover:bg-emerald-50 transition flex items-start gap-2"
                              data-testid={`benef-${i}`}
                            >
                              <div className={`w-8 h-8 rounded-full grid place-items-center font-semibold text-xs flex-shrink-0 ${b.kind === "saved" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-500"}`}>
                                {(b.beneficiary_name || "?").slice(0, 1).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                  {b.beneficiary_name}
                                  {b.kind === "saved" && <span className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">Carnet</span>}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {[b.beneficiary_phone, b.beneficiary_email, b.beneficiary_account].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                            </button>
                          ))}
                          {savedContacts.length + beneficiaries.length === 0 && (
                            <div className="p-4 text-center text-sm text-gray-500">Aucun bénéficiaire enregistré pour ce corridor.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="sl-label">Nom du bénéficiaire</label>
                    <input className="sl-input" value={form.beneficiary_name} onChange={(e) => update("beneficiary_name", sanitizeName(e.target.value))} maxLength={80} data-testid="input-beneficiary-name" />
                  </div>
                  <div>
                    <label className="sl-label">
                      Téléphone du bénéficiaire ({c.flagTo} {c.beneficiaryDialCode}){usesAccountReception ? " — optionnel" : ""}
                    </label>
                    <PhoneInput
                      value={form.beneficiary_phone}
                      onChange={(v) => { update("beneficiary_phone", v); setTrustedBeneficiary(false); }}
                      dialCode={c.beneficiaryDialCode}
                      placeholder={usesAccountReception ? "6 12 34 56 78" : "06 00 00 00"}
                      data-testid="input-beneficiary-phone"
                      error={!trustedBeneficiary && form.beneficiary_phone && form.beneficiary_phone.length > c.beneficiaryDialCode.length + 2 && !beneficiaryValidation.valid ? beneficiaryValidation.message : ""}
                      hint={!usesAccountReception ? (PHONE_VALIDATION[form.reception_method]?.hint || "") : "Mobile (6/7) ou fixe (1-5)"}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sl-label">Mode de réception</label>
                    <div className={`grid gap-3 ${usesAccountReception ? "grid-cols-1 md:grid-cols-3" : "grid-cols-2"}`}>
                      {receptionMethodsFor(form.corridor).map(([k, v]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => update("reception_method", k)}
                          className={`p-4 rounded-xl border-2 text-left transition ${
                            form.reception_method === k ? "border-[var(--sl-primary)] bg-[var(--sl-primary)]/5" : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`reception-${k}`}
                        >
                          <div className="font-semibold">{v.label}</div>
                          {v.hint && <div className="text-xs text-gray-500 mt-1">{v.hint}</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                  {usesAccountReception && recMeta?.fields?.includes("email") && (
                    <div className="md:col-span-2">
                      <label className="sl-label">Email PayPal du bénéficiaire</label>
                      <input className="sl-input" type="email" value={form.beneficiary_email} onChange={(e) => update("beneficiary_email", e.target.value)} data-testid="input-beneficiary-email" />
                    </div>
                  )}
                  {usesAccountReception && recMeta?.fields?.includes("account") && (
                    <div className="md:col-span-2">
                      <label className="sl-label">
                        {form.reception_method === "virement_instantane"
                          ? "IBAN du bénéficiaire"
                          : `Identifiant Wero (téléphone 🇫🇷 ${c.beneficiaryDialCode} ou email)`}
                      </label>
                      <input
                        className="sl-input font-mono"
                        value={form.beneficiary_account}
                        onChange={(e) => update("beneficiary_account", e.target.value)}
                        placeholder={form.reception_method === "virement_instantane" ? "FR76 XXXX XXXX XXXX XXXX XXXX XXX" : `${c.beneficiaryDialCode} 6 XX XX XX XX`}
                        data-testid="input-beneficiary-account"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentSemantic === "Montant" && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl font-semibold">Montant</h3>

                {/* Direction toggle: enter what you send OR what the beneficiary receives */}
                <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-[var(--sl-bg-alt)] border border-gray-200" data-testid="amount-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setAmountMode("send")}
                    className={`py-2 rounded-lg text-sm font-semibold transition ${amountMode === "send" ? "bg-white shadow-sm text-[var(--sl-primary)]" : "text-gray-500"}`}
                    data-testid="amount-mode-send"
                  >
                    J&apos;envoie ({c.source_currency})
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAmountMode("receive"); if (quote?.amount_xaf != null && !receiveInput) setReceiveInput(String(quote.amount_xaf)); }}
                    className={`py-2 rounded-lg text-sm font-semibold transition ${amountMode === "receive" ? "bg-white shadow-sm text-[var(--sl-primary)]" : "text-gray-500"}`}
                    data-testid="amount-mode-receive"
                  >
                    Le bénéficiaire reçoit ({c.target_currency})
                  </button>
                </div>

                {amountMode === "send" ? (
                  <div>
                    <label className="sl-label">Montant en {c.source_currency}</label>
                    <input
                      type="number"
                      min={minDepositFor(form.corridor)}
                      className="sl-input text-2xl font-heading"
                      value={form.amount_xof}
                      onChange={(e) => update("amount_xof", e.target.value)}
                      placeholder={isFRGA ? "Ex. 50" : (c.source_currency === "XOF" ? "Ex. 50000" : "Ex. 100000")}
                      data-testid="input-amount"
                    />
                    <div className="text-xs text-gray-500 mt-1.5">
                      Montant minimum : <span className="font-semibold">{minDepositFor(form.corridor).toLocaleString("fr-FR")} {c.source_currency}</span>.
                    </div>
                    {form.amount_xof && parseFloat(form.amount_xof) > 0 && parseFloat(form.amount_xof) < minDepositFor(form.corridor) && (
                      <div className="text-xs text-red-600 mt-1.5" data-testid="amount-min-warning">
                        Le montant doit être au moins de {minDepositFor(form.corridor).toLocaleString("fr-FR")} {c.source_currency}.
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="sl-label">Montant reçu par le bénéficiaire en {c.target_currency}</label>
                    <input
                      type="number"
                      min={1}
                      className="sl-input text-2xl font-heading"
                      value={receiveInput}
                      onChange={(e) => setReceiveInput(e.target.value)}
                      placeholder={c.target_currency === "EUR" ? "Ex. 100" : "Ex. 50000"}
                      data-testid="input-amount-receive"
                    />
                    {quote && receiveInput && parseFloat(receiveInput) > 0 ? (
                      <div className="text-xs text-gray-600 mt-1.5" data-testid="receive-computed-send">
                        Vous devrez envoyer <span className="font-semibold text-[var(--sl-primary)]">{formatAmount(quote.amount_xof, c.source_currency)}</span> (frais inclus).
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 mt-1.5">
                        Saisissez le montant exact que votre proche doit recevoir.
                      </div>
                    )}
                    {receiveInput && parseFloat(receiveInput) > 0 && !quote && (
                      <div className="text-xs text-red-600 mt-1.5" data-testid="receive-min-warning">
                        Ce montant est trop faible : l&apos;envoi minimum est de {minDepositFor(form.corridor).toLocaleString("fr-FR")} {c.source_currency}.
                      </div>
                    )}
                  </div>
                )}
                {quote && (
                  <div className="rounded-xl bg-[var(--sl-bg-alt)] p-5 space-y-2 text-sm" data-testid="quote-breakdown">
                    <div className="flex justify-between"><span className="text-gray-600">Montant</span><span className="font-medium">{formatAmount(quote.amount_xof, c.source_currency)}</span></div>
                    {quote.referral?.applied ? (
                      <>
                        <div className="flex justify-between text-gray-500 line-through">
                          <span>Frais ({quote.fee_percent}%{quote.fee_fixed ? ` + ${formatAmount(quote.fee_fixed, c.source_currency)}` : ""})</span>
                          <span>- {formatAmount(quote.referral.original_fee, c.source_currency)}</span>
                        </div>
                        <div className="flex justify-between" data-testid="quote-fee-discounted">
                          <span className="text-emerald-700 font-medium">Frais après code parrainage (-15%)</span>
                          <span className="font-semibold text-emerald-700">- {formatAmount(quote.fee, c.source_currency)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between"><span className="text-gray-600">Frais ({quote.fee_percent}%{quote.fee_fixed ? ` + ${formatAmount(quote.fee_fixed, c.source_currency)}` : ""})</span><span className="font-medium text-amber-700">- {formatAmount(quote.fee, c.source_currency)}</span></div>
                    )}
                    <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-600">Net envoyé</span><span className="font-semibold">{formatAmount(quote.net_xof, c.source_currency)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Taux {c.source_currency} → {c.target_currency}</span><span className="font-mono text-xs">× {quote.exchange_rate}</span></div>
                    <div className="flex justify-between text-lg pt-2 border-t border-gray-200">
                      <span className="font-heading font-bold">Reçu {receiverLocLabel}</span>
                      <span className="font-heading font-bold text-[var(--sl-primary)]">{formatAmount(quote.amount_xaf, c.target_currency)}</span>
                    </div>
                  </div>
                )}

                {/* Promo / referral code field — under new "volume-cumul" system,
                    code can be used on ANY amount for ga_fr & ga_rw (5 % discount on fees). */}
                {(isFR || isRW) && parseFloat(form.amount_xof) > 0 && (
                  <div data-testid="referral-field-wrap">
                    <label className="sl-label flex items-center gap-2">
                      Code de parrainage <span className="text-xs text-gray-400 font-normal">(optionnel — {referralDiscount} % de remise sur les frais)</span>
                    </label>
                    <input
                      type="text"
                      value={form.referral_code}
                      onChange={(e) => update("referral_code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                      placeholder="Ex. SLA1B2C3"
                      maxLength={12}
                      className="sl-input font-mono tracking-wider uppercase"
                      data-testid="input-referral-code"
                    />
                    {form.referral_code && quote && (
                      quote.referral?.applied ? (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="referral-applied">
                          <CheckCircle2 size={12} /> Code valide — remise de {quote.referral.discount_percent} % appliquée
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-amber-700" data-testid="referral-rejected">
                          {quote.referral?.reason || "Code non reconnu."}
                        </div>
                      )
                    )}
                  </div>
                )}
                {requiresKyc && (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
                    data-testid="kyc-notice"
                  >
                    <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
                    <span>
                      Ce montant nécessite une <span className="font-semibold">vérification d'identité</span> à l'étape suivante (CNI, passeport ou permis). Préparez votre pièce d'identité.
                    </span>
                  </div>
                )}
              </div>
            )}

            {currentSemantic === "KYC" && (
              <div className="space-y-5" data-testid="kyc-step">
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                  <ShieldCheck size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Vérification d'identité (KYC) obligatoire</div>
                    <div className="text-xs mt-1">
                      Conformément à nos règles de lutte contre la fraude, les transactions à partir de <span className="font-semibold">300 000 FCFA</span> nécessitent une pièce d'identité. Vos informations sont confidentielles et consultées uniquement par l'équipe Swiftlink-Africa.
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="sl-label">Type de pièce</label>
                    <select
                      className="sl-input"
                      value={form.kyc_doc_type}
                      onChange={(e) => update("kyc_doc_type", e.target.value)}
                      data-testid="kyc-doc-type"
                    >
                      <option value="cni">{KYC_DOC_LABELS.cni}</option>
                      <option value="passeport">{KYC_DOC_LABELS.passeport}</option>
                      <option value="permis">{KYC_DOC_LABELS.permis}</option>
                    </select>
                  </div>
                  <div>
                    <label className="sl-label">Numéro de la pièce</label>
                    <input
                      className="sl-input font-mono"
                      value={form.kyc_doc_number}
                      onChange={(e) => update("kyc_doc_number", e.target.value)}
                      placeholder="Ex. 123456789"
                      data-testid="kyc-doc-number"
                    />
                  </div>
                </div>
                <div>
                  <label className="sl-label">Nom complet figurant sur la pièce</label>
                  <input
                    className="sl-input"
                    value={form.kyc_full_name}
                    onChange={(e) => update("kyc_full_name", sanitizeName(e.target.value))}
                    maxLength={80}
                    placeholder="Ex. NDONG MOUSSAVOU Jean-Pierre"
                    data-testid="kyc-full-name"
                  />
                </div>

                <div>
                  <label className="sl-label flex items-center gap-1.5">
                    <IdCard size={14} /> Photo / scan de la pièce <span className="text-red-500">*</span>
                  </label>
                  <KycUpload
                    value={form.kyc_doc_path}
                    onChange={(v) => update("kyc_doc_path", v)}
                  />
                  <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1">
                    <FileText size={12} className="flex-shrink-0 mt-0.5" />
                    PNG, JPG, WEBP, HEIC ou PDF — max 8 Mo. Recto-verso si CNI.
                  </p>
                </div>
              </div>
            )}

            {/* placeholder anchor — Dépôt step follows below */}
            {currentSemantic === "Dépôt" && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl font-semibold">
                  {isFRGA ? "Payez via PayPal (mode Entre proches)" : `Effectuez le dépôt ${senderLocLabel}`}
                </h3>
                <div className={`grid gap-3 ${depositMethodsFor(form.corridor).length > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}>
                  {depositMethodsFor(form.corridor).map(([k, v]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => update("deposit_method", k)}
                      className={`p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                        form.deposit_method === k ? "border-[var(--sl-primary)] bg-[var(--sl-primary)]/5" : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`deposit-${k}`}
                    >
                      <BrandLogo method={k} size={36} />
                      <div>
                        <div className="font-semibold">{v.label}</div>
                        <div className="text-xs text-gray-500 mt-1">Au nom de {v.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="rounded-xl border-2 border-dashed border-[var(--sl-secondary)] bg-amber-50/40 p-5">
                  {(() => {
                    const isWeroDep = isFRGA && form.deposit_method === "wero_send";
                    const frgaValue = isWeroDep
                      ? (frgaAccounts?.wero_number || dep.wero_number)
                      : (frgaAccounts?.paypal_email || dep.paypal_email);
                    const frgaName = isWeroDep ? (frgaAccounts?.wero_name || dep.name) : dep.name;
                    const shownValue = isFRGA ? frgaValue : dep.number;
                    const shownName = isFRGA ? frgaName : dep.name;
                    return (
                  <div className="flex items-start gap-4">
                    <BrandLogo method={form.deposit_method} size={48} />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-widest font-semibold text-[var(--sl-secondary-dark)]">
                        {isFRGA ? (isWeroDep ? "Envoyez via Wero à ce numéro" : "Envoyez via PayPal à cette adresse") : `Envoyez vers ce numéro ${dep.label}`}
                      </div>
                      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0">
                          <div className={`font-heading font-bold tracking-tight break-words leading-snug ${(shownValue || "").length > 18 ? "text-lg sm:text-xl" : "text-2xl sm:text-3xl"}`}>
                            {shownValue}
                          </div>
                          <div className="text-sm text-gray-600 mt-0.5">{shownName}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText((shownValue || "").replace(/\s/g, "")); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                          className="sl-btn-outline !py-2 !px-4 text-sm self-start sm:self-auto flex-shrink-0"
                          data-testid="copy-deposit-number"
                        >
                          {copied ? <><CheckCircle2 size={16} /> Copié</> : <><Copy size={16} /> Copier</>}
                        </button>
                      </div>
                      {quote && (
                        <div className="mt-3 text-sm">
                          Montant à envoyer : <span className="font-bold">{formatAmount(quote.amount_xof, c.source_currency)}</span>
                        </div>
                      )}
                      {isFRGA && dep.instructions && (
                        <div className={`mt-4 rounded-lg text-sm p-3 leading-relaxed border ${isWeroDep ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="paypal-ff-warning">
                          <span className="font-semibold">⚠️ {dep.instructions}</span>
                        </div>
                      )}
                      {isFRGA && !isWeroDep && (
                        <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3.5" data-testid="larry-fallback-card">
                          <div className="flex items-start gap-3">
                            <div className="text-2xl leading-none flex-shrink-0">💬</div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-blue-900">Pas de compte PayPal ?</div>
                              <div className="text-xs text-blue-800 mt-0.5 leading-relaxed">
                                Notre Agent Larry vous accompagne directement sur WhatsApp (+33 7 53 72 81 95) pour finaliser votre échange.
                              </div>
                            </div>
                          </div>
                          <a
                            href={SUPPORT_AGENT_WA}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-white text-xs font-semibold px-3 py-2.5 hover:brightness-110 whitespace-nowrap w-full"
                            data-testid="contact-larry-btn"
                          >
                            Je n&apos;ai pas PayPal
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                </div>
                <div>
                  <label className="sl-label">{isFRGA ? "Capture d'écran du paiement PayPal" : "Capture d'écran du dépôt"} <span className="text-red-500">*</span></label>
                  <ProofUpload
                    value={form.proof_path}
                    onChange={(v) => update("proof_path", v)}
                    data-testid="proof-upload"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    {isFRGA
                      ? "Téléversez la capture du paiement PayPal montrant clairement « Entre proches / Friends & Family » et le montant en euros."
                      : "Téléversez la capture du SMS Mobile Money ou de votre app opérateur confirmant le dépôt. Cette capture est obligatoire et tient lieu de preuve."}
                  </p>
                </div>
                <div>
                  <label className="sl-label">Note (optionnel)</label>
                  <textarea className="sl-input" rows="2" value={form.note} onChange={(e) => update("note", e.target.value)} data-testid="input-note" />
                </div>
              </div>
            )}

            {currentSemantic === "Confirmation" && (
              <div className="space-y-5" data-testid="confirmation-step">
                <h3 className="font-heading text-xl font-semibold">Récapitulatif</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <Field label="Corridor" value={c.label} />
                  <Field label="Expéditeur" value={`${form.sender_name} · ${form.sender_phone}`} />
                  <Field label="Bénéficiaire" value={`${form.beneficiary_name}${form.beneficiary_phone ? " · " + form.beneficiary_phone : ""}`} />
                  <Field label="Méthode dépôt" value={DEPOSIT_METHODS[form.deposit_method].label} />
                  <Field label="Méthode réception" value={RECEPTION_METHODS[form.reception_method].label} />
                  {form.beneficiary_email && <Field label="Email PayPal" value={form.beneficiary_email} />}
                  {form.beneficiary_account && <Field label={form.reception_method === "virement_instantane" ? "IBAN" : "Compte Wero"} value={form.beneficiary_account} />}
                  {form.proof_path && <Field label="Capture du dépôt" value="✓ Téléversée" />}
                  {requiresKyc && form.kyc_doc_path && (
                    <Field label="KYC" value={`✓ ${KYC_DOC_LABELS[form.kyc_doc_type] || ""} · ${form.kyc_full_name}`} />
                  )}
                  {form.note && <Field label="Note" value={form.note} />}
                </div>
                {quote && (
                  <div className="rounded-xl bg-[var(--sl-bg-alt)] p-5 grid md:grid-cols-3 gap-4 text-center">
                    <div><div className="text-xs uppercase text-gray-500">Montant</div><div className="font-heading font-bold text-lg">{formatAmount(quote.amount_xof, c.source_currency)}</div></div>
                    <div><div className="text-xs uppercase text-gray-500">Frais</div><div className="font-heading font-bold text-lg text-amber-700">{formatAmount(quote.fee, c.source_currency)}</div></div>
                    <div><div className="text-xs uppercase text-gray-500">Reçu {receiverLocLabel}</div><div className="font-heading font-bold text-lg text-[var(--sl-primary)]">{formatAmount(quote.amount_xaf, c.target_currency)}</div></div>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  En confirmant, vous certifiez avoir effectué le dépôt. Notre équipe vérifiera la transaction manuellement.
                </p>
              </div>
            )}

            {/* Nav */}
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-2 text-gray-600 disabled:opacity-30 hover:text-[var(--sl-primary)]"
                data-testid="step-back-btn"
              >
                <ArrowLeft size={16} /> Retour
              </button>
              {step < steps.length - 1 ? (
                <button
                  type="button"
                  disabled={!canNext()}
                  onClick={() => setStep((s) => s + 1)}
                  className="sl-btn-primary"
                  data-testid="step-next-btn"
                >
                  Continuer <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={submit}
                  className="sl-btn-primary"
                  data-testid="submit-transaction-btn"
                >
                  {submitting ? "Création…" : "Confirmer l'envoi"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
      <div className="font-medium text-gray-900 break-all">{value || "—"}</div>
    </div>
  );
}

function SenderRecap({ form, isFRGA, c, onEdit }) {
  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3" data-testid="sender-recap">
      <div className="w-10 h-10 rounded-full bg-emerald-600 text-white grid place-items-center font-bold flex-shrink-0">
        {(form.sender_name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Vos informations</div>
        <div className="font-semibold text-sm mt-0.5 truncate">{form.sender_name}</div>
        <div className="text-xs text-gray-600 font-mono truncate">
          {isFRGA ? form.sender_email : form.sender_phone}
        </div>
        {!isFRGA && form.sender_email && <div className="text-xs text-gray-500 truncate">{form.sender_email}</div>}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs font-semibold text-emerald-700 hover:underline whitespace-nowrap flex-shrink-0"
        data-testid="sender-recap-edit"
      >
        Modifier
      </button>
    </div>
  );
}
